// Taker /replays/:slug — engine-hosted replay player.
//
// Three unlock paths:
//   1. ?t=<replay-access-token> in the URL → auto-unlocks (the email link case)
//   2. ?email=<address> (or ?utm_email=) → silently runs the gate lookup on
//      mount and unlocks if the address matches a registration. Falls back to
//      the manual gate (with the email pre-filled) on miss. For generic
//      shareable URLs decorated with the recipient's email by marketing tools.
//   3. Email gate fallback → user pastes their email; we look up their submission
//
// On unlock, we render a signed Bunny iframe + the resolved CTA buttons. On
// failure (archived, awaiting-replay, not-registered, no-source) we render the
// matching error state with a clear next-step message.

import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { recordLandingView } from './api';
import { Block } from './components/Blocks';
import { BrandProvider } from './components/BrandContext';
import type { ContentBlock, ResolvedBrand } from './types';
import { applyBranding } from './theme';
import { interpolateBlocks } from './lib/interpolate';
import { useBrandGtm } from './lib/useBrandGtm'
import { useWatchTracker } from './lib/useWatchTracker';

// Event-time + replay-close template vars the server injects onto eventConfig
// (sanitizeConfigForPublic). The siteNav centerText uses {{replayClosesDate}}
// etc.; without interpolating them here the replay page renders the literal
// token in its header (incident 2026-07-13). Mirror of App.tsx's knownKeys.
const EVENT_NAV_VARS = [
  'eventDayLong', 'eventTimeShort', 'eventTimeRange', 'eventDateTimeFull',
  'eventDuration', 'eventDateOrdinal', 'eventWeekday', 'eventDayPart', 'eventDayRelative', 'eventDayRelativeLower',
  'eventUrl', 'bookingUrl', 'replayUrl',
  'replayClosesShort', 'replayClosesDay', 'replayClosesDate',
  'replayClosesOrdinal', 'replayClosesRelative', 'replayClosesDateTimeFull',
] as const;

const API = import.meta.env.VITE_API_BASE_URL as string;

interface ResolvedCta {
  kind: 'booking' | 'program' | 'custom';
  label: string;
  url: string;
  /** One-line caption below the button (e.g. "30 min, no pitch"). */
  hint?: string;
}

/** Lightweight read of the public assessment config, used by ReplayPage to
 *  borrow the LP's siteNav + footer so the gate/player feels branded instead
 *  of landing in a stripped-down white card. Only the bits we need. */
interface BrandShell {
  branding: Record<string, unknown> | null;
  brand: ResolvedBrand | null;
  navBlock: ContentBlock | null;
  footerBlock: ContentBlock | null;
  /** What the gate needs to explain the replay before the visitor is known.
   *  All from the public assessment config — nothing gated leaks here. */
  title: string | null;
  recordedOn: string | null;
  closesOn: string | null;
  duration: string | null;
  heroSubhead: string | null;
  hostBlock: ContentBlock | null;
  gateHeadline: string | null;
  gateSubheading: string | null;
  lede: string | null;
  offer: ReplayOffer | null;
}

const EMPTY_SHELL: BrandShell = {
  branding: null, brand: null, navBlock: null, footerBlock: null,
  title: null, recordedOn: null, closesOn: null, duration: null, heroSubhead: null,
  hostBlock: null, gateHeadline: null, gateSubheading: null, lede: null, offer: null,
};

/** The event's own replay offer, resolved by the worker (replay-page.ts):
 *  null when none is set or its end day has passed. */
interface ReplayOffer {
  eyebrow: string;
  headline: string;
  body: string | null;
  valueLabel: string | null;
  endsOn: string | null;
  endsLabel: string | null;
  ctaLabel: string;
  ctaUrl: string;
  hint: string | null;
}

interface ReplayPagePayload {
  /** Small label above the headline. e.g. "Replay · The Clients Blueprint Live" */
  eyebrow: string | null;
  /** Main page heading. e.g. "Welcome back." */
  heading: string | null;
  /** Optional meta line — may contain {{duration}}. */
  subheading: string | null;
  /** Lede paragraph rendered above the player. May contain {{duration}}. */
  lede: string | null;
  /** Bridge copy below the player (e.g. host's note tying workshop to offer). */
  bridgeText: string | null;
  /** Attribution line for the bridge text (e.g. "— Lisa"). */
  bridgeAttribution: string | null;
  blocksAbove: unknown[];
  blocksBelow: unknown[];
  ctas: ResolvedCta[];
  /** Absent on payloads from before 2026-10-09; null when none / ended. */
  offer?: ReplayOffer | null;
}

type AccessResponse =
  | {
      ok: true;
      signedIframeUrl: string | null;
      externalUrl: string | null;
      thumbnailUrl: string | null;
      /** Recording length in seconds, when Bunny exposes it. */
      durationSeconds: number | null;
      /** Signed watch-session token — present on real (non-preview) unlocks.
       *  The player echoes it on progress heartbeats so we capture watch depth. */
      watchSession: { token: string } | null;
      replayPage: ReplayPagePayload;
      title: string;
    }
  | {
      ok: false;
      // 'archived' + 'awaiting-replay' both redirect to /take/:slug — the LP's
      // blocksByPhase.archived / blocksByPhase.replay provide the full content
      // for those states.
      reason: 'archived' | 'awaiting-replay' | 'not-registered' | 'no-source';
      successorSlug?: string | null;
    };

export default function ReplayPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? '';
  // Accept both `?email=` and `?utm_email=` so marketing-tool decorations
  // (which often standardize on the utm_ namespace) work without a config flip.
  const urlEmail = (searchParams.get('email') ?? searchParams.get('utm_email') ?? '').trim();

  // Archived + awaiting-replay both redirect to /take/:slug — they're modelled
  // as terminal "loading" until the redirect lands.
  const [state, setState] = useState<'loading' | 'gate' | 'unlocked' | 'no-source'>('loading');
  const [data, setData] = useState<Extract<AccessResponse, { ok: true }> | null>(null);
  const [gateEmail, setGateEmail] = useState(urlEmail);
  const [gateBusy, setGateBusy] = useState(false);
  const [gateErr, setGateErr] = useState('');
  // When true, the gate renders a soft "Join the next live session →" link
  // alongside the error — point of escape for visitors whose email isn't on
  // the registration list (forwarded link, typo, never registered).
  const [notRegistered, setNotRegistered] = useState(false);
  // On-the-spot registration from the gate. When the entered email isn't on the
  // list, a new visitor can register right here (name + the email they typed) via
  // /api/embed/register, then we immediately re-run access to unlock — no bounce
  // to the landing page. This is the post-event RSVP path: a fresh register in
  // replay phase lands on the replay track (see effectivePhase in embed-routes).
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regBusy, setRegBusy] = useState(false);
  const [regErr, setRegErr] = useState('');
  const [brandShell, setBrandShell] = useState<BrandShell>(EMPTY_SHELL);
  const [shellSettled, setShellSettled] = useState(false);

  // Landing-views beacon — fires once per mount for funnel tracking.
  useEffect(() => {
    if (slug) recordLandingView(slug);
  }, [slug]);

  // Brand-aware GTM — replay landings are a tracked funnel step. Waits for
  // the shell fetch to settle so a non-TSB brand is never double-injected;
  // if the fetch failed, the host fallback inside the hook still fires.
  useBrandGtm(brandShell.brand?.gtmContainerId, shellSettled);

  // Pull the assessment's public config so the gate + player render with
  // the same brand chrome as /take/:slug — siteNav at top, footer at bottom,
  // brand palette applied. Independent of the access call so the chrome is
  // visible immediately, regardless of auth state.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/assessments/${encodeURIComponent(slug)}?phase=replay-loaded`);
        if (!res.ok) return;
        const json = await res.json() as { assessment?: { title?: string; config?: Record<string, unknown>; brand?: Record<string, unknown> | null } };
        const cfg = json.assessment?.config ?? {};
        const branding = (cfg as { branding?: Record<string, unknown> }).branding ?? null;
        const intro = (cfg as { intro?: { blocks?: ContentBlock[] } }).intro ?? {};
        const blocks = intro.blocks ?? [];
        const rawNavBlock = blocks.find((b) => b.type === 'siteNav') ?? null;
        // Interpolate the nav's {{replayClosesDate}} / event-time tokens from the
        // eventConfig vars the server injected — same treatment /take/:slug gives
        // its persistent nav. Without this the header shows the literal token.
        const ec = (cfg as { eventConfig?: Record<string, unknown> }).eventConfig ?? {};
        const navVars: Record<string, string> = {};
        for (const k of EVENT_NAV_VARS) {
          if (typeof ec[k] === 'string' && ec[k]) navVars[k] = ec[k] as string;
        }
        const navBlock = rawNavBlock && Object.keys(navVars).length
          ? interpolateBlocks([rawNavBlock], navVars)[0]
          : rawNavBlock;
        // Footer: the event's eventFooter block when it has one (every event
        // since the summer); older landings used a trailing html block.
        const htmlBlocks = blocks.filter((b) => b.type === 'html');
        const footerBlock = blocks.find((b) => b.type === 'eventFooter')
          ?? (htmlBlocks.length > 0 ? htmlBlocks[htmlBlocks.length - 1] : null);
        const brand = (json.assessment?.brand ?? null) as ResolvedBrand | null;
        const rp = (ec.replayPage ?? {}) as Record<string, unknown>;
        const gate = (rp.gate ?? {}) as Record<string, unknown>;
        const hero = blocks.find((b) => b.type === 'eventHero') as unknown as Record<string, unknown> | undefined;
        const s = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
        if (!cancelled) {
          setBrandShell({
            branding, brand, navBlock, footerBlock,
            title: s(json.assessment?.title) ?? s(ec.eventTitle),
            recordedOn: s(ec.eventDayLong),
            closesOn: s(ec.replayClosesDate),
            duration: s(ec.eventDuration),
            // The landing hero's subhead describes the session; strip the
            // {{em:…}} / {{strong:…}} markers it may carry.
            heroSubhead: s(hero?.subhead)?.replace(/\{\{(?:em|strong):([^}]*)\}\}/g, '$1') ?? null,
            hostBlock: blocks.find((b) => b.type === 'hostBio') ?? null,
            gateHeadline: s(gate.headline),
            gateSubheading: s(gate.subheading),
            lede: s(rp.lede),
            offer: (rp.offer && typeof rp.offer === 'object' ? rp.offer : null) as ReplayOffer | null,
          });
          if (branding) {
            try {
              applyBranding(branding as never, { brand: brand as never });
            } catch { /* ignore */ }
          }
        }
      } catch { /* non-fatal */ }
    })().finally(() => { if (!cancelled) setShellSettled(true); });
    return () => { cancelled = true; };
  }, [slug]);

  // Auto-unlock on mount. Token wins (cryptographic). Email URL param is the
  // soft path — silently runs the gate lookup; falls through to the manual
  // gate (with the email pre-filled) if the address isn't on the list.
  useEffect(() => {
    if (!slug) return;
    if (token) {
      void fetchAccess(slug, { token });
      return;
    }
    if (urlEmail) {
      void fetchAccess(slug, { email: urlEmail });
      return;
    }
    setState('gate');
  }, [slug, token, urlEmail]);

  async function fetchAccess(s: string, body: { token?: string; email?: string }): Promise<void> {
    setGateErr('');
    setNotRegistered(false);
    if (body.email !== undefined) setGateBusy(true);
    try {
      const res = await fetch(`${API}/api/public/replays/${encodeURIComponent(s)}/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = (await res.json()) as AccessResponse;
      if (json.ok) {
        setData(json);
        setState('unlocked');
        return;
      }
      // Not ok — pick state from reason.
      if (json.reason === 'archived') {
        // Closed state lives on the marketing LP (intro.blocksByPhase.archived) —
        // it's the RSVP-for-next-cohort design with host bio, FAQ, footer, the
        // full handoff experience. Bounce there instead of rendering a minimal
        // "Replay closed" card on the /replays surface.
        window.location.replace(`/take/${encodeURIComponent(s)}`);
        return;
      } else if (json.reason === 'awaiting-replay') {
        // No replay available yet — bounce to the marketing landing where the
        // proper "Replay coming soon" blocks live (intro.blocksByPhase.replay).
        // The /replays page only renders the player; that "coming soon" content
        // belongs on /take/:slug.
        window.location.replace(`/take/${encodeURIComponent(s)}`);
        return;
      } else if (json.reason === 'not-registered') {
        setGateErr("We don't see that email on the registration list.");
        setNotRegistered(true);
        setState('gate');
      } else if (json.reason === 'no-source') {
        setState('no-source');
      } else {
        setGateErr('Something went wrong. Try again, or email support.');
        setState('gate');
      }
    } catch (err) {
      console.error('replay access failed', err);
      setGateErr('Network error. Try again in a moment.');
      setState('gate');
    } finally {
      setGateBusy(false);
    }
  }

  function handleGateSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!slug) return;
    if (!gateEmail.trim()) return;
    void fetchAccess(slug, { email: gateEmail.trim() });
  }

  // Register a brand-new visitor from the gate, then unlock. Reuses the same
  // /api/embed/register endpoint the landing-page late-register modal uses, so
  // the registrant gets the replay-track email sequence + Komunily/HubSpot sync
  // exactly as a landing-page RSVP would. On success we re-run access with the
  // now-registered email to drop them straight onto the player.
  async function handleLateRegister(e: React.FormEvent) {
    e.preventDefault();
    if (!slug) return;
    const email = gateEmail.trim();
    const firstName = regFirstName.trim();
    const lastName = regLastName.trim();
    if (!email || !firstName || !lastName) {
      setRegErr('Please enter your first and last name.');
      return;
    }
    setRegErr('');
    setRegBusy(true);
    try {
      const res = await fetch(`${API}/api/embed/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, email, firstName, lastName, source: 'replay-gate-late-register' }),
      });
      if (!res.ok) {
        // 410 = event archived (registration closed); anything else is a
        // transient/validation failure. Surface a plain message either way.
        const msg = res.status === 410
          ? 'Registration for this session has closed.'
          : "We couldn't complete your registration. Please try again.";
        setRegErr(msg);
        setRegBusy(false);
        return;
      }
      // Registered — unlock immediately with the same email. fetchAccess flips
      // state to 'unlocked' on success and clears the not-registered flag.
      await fetchAccess(slug, { email });
    } catch (err) {
      console.error('replay gate late-register failed', err);
      setRegErr('Network error. Try again in a moment.');
    } finally {
      setRegBusy(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────

  if (state === 'loading') return <ReplayShell><CenteredMessage>Loading replay…</CenteredMessage></ReplayShell>;

  if (state === 'no-source') {
    return (
      <ReplayShell brandShell={brandShell} navCta={null}>
        <CenteredMessage>
          The replay isn't available yet. Check back later, or watch your inbox for the link.
        </CenteredMessage>
      </ReplayShell>
    );
  }

  if (state === 'gate') {
    // The LP (/events/:slug) is the right escape for any visitor who can't
    // unlock here — it has both a primary "Watch the Replay" CTA (for guests
    // who registered) AND a secondary "Didn't Register? Get the replay + RSVP"
    // modal that POSTs /api/embed/register and redirects them back with their
    // email pre-filled. Surface the link both on initial load (soft "didn't
    // register yet?" footer) and after a not-registered miss (promoted to
    // primary CTA, with the email input demoted).
    const lpHref = slug
      ? `/events/${encodeURIComponent(slug)}${gateEmail.trim() ? `?email=${encodeURIComponent(gateEmail.trim())}` : ''}`
      : null;
    return (
      <ReplayShell brandShell={brandShell} navCta={null}>
        <GateLayout shell={brandShell} slug={slug ?? ''}>
        <GateForm
          email={gateEmail}
          busy={gateBusy}
          err={gateErr}
          onChange={setGateEmail}
          onSubmit={handleGateSubmit}
          lpHref={lpHref}
          isNotRegistered={notRegistered}
          regFirstName={regFirstName}
          regLastName={regLastName}
          regBusy={regBusy}
          regErr={regErr}
          onRegFirstName={setRegFirstName}
          onRegLastName={setRegLastName}
          onRegister={handleLateRegister}
        />
        </GateLayout>
      </ReplayShell>
    );
  }

  // unlocked
  if (!data) return null;
  const hasNextStep = !!data.replayPage.offer || data.replayPage.ctas.length > 0;
  return (
    <ReplayShell
      brandShell={brandShell}
      navCta={hasNextStep ? { label: 'Your next step', url: '#next-step' } : null}
    >
      <Player data={data} slug={slug ?? ''} />
    </ReplayShell>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function ReplayShell({
  children,
  brandShell,
  navCta,
}: {
  children: React.ReactNode;
  brandShell?: BrandShell;
  /** The nav button on this page. The landing's own nav carries pre-event
   *  buttons ("Register Now") that are wrong once the event is over, so the
   *  replay page always sets its own: "Your next step" on the unlocked
   *  player, nothing on the gate (the form is the action). */
  navCta?: { label: string; url: string } | null;
}) {
  const brand = brandShell?.brand ?? null;
  const rawNav = brandShell?.navBlock;
  // Replay chrome on the landing's nav: the centre strip names the replay and
  // its close date instead of the live date and time, and the button is ours.
  // Clone so the shared block reference is never mutated.
  const centerText = brandShell?.title
    ? `{{strong:${brandShell.title}}} · Replay${brandShell.closesOn ? ` · Available through ${brandShell.closesOn}` : ''}`
    : undefined;
  const navBlock = rawNav
    ? ({
        ...(rawNav as unknown as Record<string, unknown>),
        ...(centerText ? { centerText } : {}),
        cta: navCta ?? null,
        ctaByPhase: undefined,
        navLinks: undefined,
      } as unknown as ContentBlock)
    : rawNav;
  const footerBlock = brandShell?.footerBlock;
  return (
    <BrandProvider brand={brand}>
      <div style={{
        minHeight: '100vh',
        // Use the LP background when branding is loaded; fall back to neutral
        // while the brand fetch is still in flight.
        background: 'var(--color-bg, #FAFAF7)',
        color: 'var(--color-text, #0E1E3A)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Branded site nav — logo + center label from the resolved brand
            and the LP's authored siteNav block. CTA hidden on gate. */}
        {navBlock && <Block block={navBlock} />}
        <div style={{
          flex: 1,
          padding: '24px 16px 64px',
        }}>
          <div style={{ maxWidth: 880, margin: '0 auto' }}>
            {children}
          </div>
        </div>
        {/* Branded footer (privacy/terms/contact) — same chrome as the LP. */}
        {footerBlock && <Block block={footerBlock} />}
      </div>
    </BrandProvider>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      textAlign: 'center', padding: '60px 24px',
      color: '#6B6B6B', fontSize: 16,
    }}>
      {children}
    </div>
  );
}

function GateForm({
  email,
  busy,
  err,
  onChange,
  onSubmit,
  lpHref,
  isNotRegistered,
  regFirstName,
  regLastName,
  regBusy,
  regErr,
  onRegFirstName,
  onRegLastName,
  onRegister,
}: {
  email: string
  busy: boolean
  err: string
  onChange: (v: string) => void
  onSubmit: (e: React.FormEvent) => void
  /** Link to the LP (/events/:slug) — carries the typed email forward so the
   *  LP's late-register modal prefills it. Used both for the initial soft
   *  "didn't register yet?" footer link AND the promoted CTA on the
   *  not-registered miss state. */
  lpHref?: string | null
  /** True once the gate confirms the email isn't on the registration list.
   *  Promotes on-the-spot registration to the primary action. */
  isNotRegistered?: boolean
  /** On-the-spot registration fields + handler, shown in the not-registered
   *  state so a brand-new visitor can register and unlock without leaving. */
  regFirstName: string
  regLastName: string
  regBusy: boolean
  regErr: string
  onRegFirstName: (v: string) => void
  onRegLastName: (v: string) => void
  onRegister: (e: React.FormEvent) => void
}) {
  // On the not-registered miss, lead with an inline register form (name + the
  // already-typed email) that unlocks in place. The LP link stays as a quieter
  // fallback for anyone who'd rather go to the full workshop page.
  if (isNotRegistered) {
    const inputStyle: React.CSSProperties = {
      width: '100%', padding: '12px 14px', fontSize: 15, boxSizing: 'border-box',
      border: '1px solid #E5E3DF', borderRadius: 8, marginBottom: 12,
      background: '#FAFAF7', color: '#0E1E3A',
    };
    return (
      <form
        onSubmit={onRegister}
        style={{
          background: '#FFFFFF', border: '1px solid #E5E3DF',
          borderRadius: 14, padding: '36px 32px',
          maxWidth: 480, margin: '0 auto', width: '100%', boxSizing: 'border-box',
        }}
      >
        <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 28, margin: '0 0 6px', fontWeight: 600 }}>
          New here? Watch the replay
        </h1>
        <p style={{ fontSize: 14, color: '#6B6B6B', margin: '0 0 24px', lineHeight: 1.5 }}>
          We don't see <strong>{email}</strong> on the list yet. Add your name and
          we'll unlock the recording right now — and send you the next live date too.
        </p>
        <input
          type="text" autoFocus required placeholder="First name"
          value={regFirstName} onChange={(e) => onRegFirstName(e.target.value)}
          disabled={regBusy} style={inputStyle}
        />
        <input
          type="text" required placeholder="Last name"
          value={regLastName} onChange={(e) => onRegLastName(e.target.value)}
          disabled={regBusy} style={inputStyle}
        />
        <button
          type="submit"
          disabled={regBusy || !regFirstName.trim() || !regLastName.trim()}
          style={{
            width: '100%', padding: '14px 18px', fontSize: 15, fontWeight: 700,
            background: 'var(--color-primary, #F05A28)', color: 'white', border: 'none', borderRadius: 8,
            cursor: regBusy ? 'not-allowed' : 'pointer',
          }}
        >
          {regBusy ? 'Unlocking…' : 'Register & watch replay →'}
        </button>
        {regErr && (
          <div style={{
            marginTop: 14, padding: '10px 12px',
            background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.25)',
            borderRadius: 6, fontSize: 13, color: '#991b1b',
          }}>{regErr}</div>
        )}
        <div style={{ marginTop: 18, textAlign: 'center', fontSize: 14, color: '#3F3F46' }}>
          Used a different email?{' '}
          <button
            type="button"
            onClick={() => onChange('')}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary, #F05A28)', fontWeight: 600, cursor: 'pointer', font: 'inherit' }}
          >
            Try that one →
          </button>
        </div>
        {lpHref && (
          <div style={{ marginTop: 10, textAlign: 'center', fontSize: 13, color: '#9A9A94' }}>
            <a href={lpHref} style={{ color: '#9A9A94', textDecoration: 'underline' }}>
              See the full workshop page
            </a>
          </div>
        )}
      </form>
    );
  }
  return (
    <form
      onSubmit={onSubmit}
      style={{
        background: '#FFFFFF',
        border: '1px solid #E5E3DF',
        borderRadius: 14, padding: '36px 32px',
        maxWidth: 480, margin: '0 auto', width: '100%', boxSizing: 'border-box',
      }}
    >
      <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 28, margin: '0 0 6px', fontWeight: 600 }}>
        Watch the replay
      </h1>
      <p style={{ fontSize: 14, color: '#6B6B6B', margin: '0 0 24px', lineHeight: 1.5 }}>
        Enter the email you registered with and the recording plays right here.
      </p>

      <input
        type="email"
        autoFocus
        required
        placeholder="you@example.com"
        value={email}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
        style={{
          width: '100%', padding: '12px 14px', fontSize: 15, boxSizing: 'border-box',
          border: '1px solid #E5E3DF',
          borderRadius: 8, marginBottom: 12,
          background: '#FAFAF7',
          color: '#0E1E3A',
        }}
      />
      <button
        type="submit"
        disabled={busy || !email.trim()}
        style={{
          width: '100%', padding: '12px 18px', fontSize: 14, fontWeight: 600,
          background: 'var(--color-primary, #F05A28)',
          color: 'white',
          border: 'none',
          borderRadius: 8, cursor: busy ? 'not-allowed' : 'pointer',
        }}
      >
        {busy ? 'Checking…' : 'Watch replay →'}
      </button>
      {err && (
        <div style={{
          marginTop: 14, padding: '10px 12px',
          background: 'rgba(220,38,38,0.06)',
          border: '1px solid rgba(220,38,38,0.25)',
          borderRadius: 6, fontSize: 13, color: '#991b1b',
        }}>{err}</div>
      )}
      {/* Initial-state soft link out to the LP for visitors who haven't
          registered. The not-registered miss is handled by the early-return
          register form above, so this only shows in the initial state. */}
      {lpHref && (
        <div style={{
          marginTop: 18, textAlign: 'center',
          fontSize: 14, color: '#3F3F46', lineHeight: 1.5,
        }}>
          Didn't register yet?{' '}
          <a
            href={lpHref}
            style={{ color: 'var(--color-primary, #F05A28)', fontWeight: 600, textDecoration: 'none' }}
          >
            Click here →
          </a>
        </div>
      )}
    </form>
  );
}

/** Render a duration in the human-friendly format the lede uses
 *  ("about 90 minutes"). Rounds to the nearest 5 min so a 1:17:47
 *  recording reads as "about 80 minutes" rather than "77 minutes 47
 *  seconds." Falls back to "" when no duration was returned by Bunny. */
function formatDurationApprox(secs: number | null): string {
  if (!secs || secs <= 0) return '';
  const minutes = Math.round(secs / 60);
  if (minutes < 5) return `${minutes} min`;
  // Round to nearest 5 for the approximate phrasing.
  const rounded = Math.round(minutes / 5) * 5;
  return `${rounded} minutes`;
}

/** Compact form ("90 min") for the player-meta line. */
function formatDurationCompact(secs: number | null): string {
  if (!secs || secs <= 0) return '';
  return `${Math.round(secs / 60)} min`;
}

/** Replace {{duration}} → "about 90 minutes" (approx) and
 *  {{durationCompact}} → "90 min" anywhere in a copy string. */
function interpolateDuration(text: string, secs: number | null): string {
  return text
    .replace(/\{\{duration\}\}/g, formatDurationApprox(secs))
    .replace(/\{\{durationCompact\}\}/g, formatDurationCompact(secs));
}

function Player({ data, slug }: { data: Extract<AccessResponse, { ok: true }>; slug: string }) {
  const rp = data.replayPage;
  const heading = rp.heading || `${data.title} · Replay`;
  const eyebrow = rp.eyebrow;
  const subheading = rp.subheading ? interpolateDuration(rp.subheading, data.durationSeconds) : null;
  const lede = rp.lede ? interpolateDuration(rp.lede, data.durationSeconds) : null;
  const bridge = rp.bridgeText;
  const bridgeAttribution = rp.bridgeAttribution;
  const ctas = rp.ctas;
  const offer = rp.offer ?? null;

  // Watch-depth capture: listen to the Bunny player and beacon progress.
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useWatchTracker(iframeRef, {
    heartbeatUrl: `${API}/api/public/replays/${encodeURIComponent(slug)}/heartbeat`,
    token: data.watchSession?.token ?? null,
    hasIframe: !!data.signedIframeUrl,
  });

  return (
    <>
      <header style={{ marginBottom: 28 }}>
        {eyebrow && (
          <div style={{
            fontSize: 12, fontWeight: 600, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: 'var(--color-primary, #F05A28)',
            marginBottom: 10,
          }}>
            {eyebrow}
          </div>
        )}
        <h1 style={{
          fontFamily: 'Fraunces, Georgia, serif', fontSize: 38, fontWeight: 600,
          margin: '0 0 12px', lineHeight: 1.1,
        }}>
          {heading}
        </h1>
        {lede && (
          <p style={{ fontSize: 17, color: '#3F3F46', margin: '0 0 0', lineHeight: 1.55, maxWidth: 720 }}>
            {lede}
          </p>
        )}
        {subheading && (
          <p style={{ fontSize: 14, color: '#6B6B6B', margin: '14px 0 0', lineHeight: 1.5 }}>
            {subheading}
          </p>
        )}
      </header>

      {rp.blocksAbove.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {(rp.blocksAbove as ContentBlock[]).map((b, i) => <Block key={i} block={b} />)}
        </div>
      )}

      <VideoFrame
        signedIframeUrl={data.signedIframeUrl}
        externalUrl={data.externalUrl}
        thumbnailUrl={data.thumbnailUrl}
        title={heading}
        iframeRef={iframeRef}
      />

      {/* Bridge copy + CTAs combined. The anchor lets the nav "Your next step →"
          CTA scroll here, and the visual hierarchy is: bridge → primary →
          secondary. Hint text per CTA optional. */}
      {(bridge || ctas.length > 0 || offer) && (
        <section id="next-step" style={{ marginTop: 36, scrollMarginTop: 90 }}>
          {bridge && (
            <blockquote style={{
              margin: '0 0 28px', padding: '0 0 0 20px',
              borderLeft: '3px solid var(--color-primary, #F05A28)',
              fontSize: 17, lineHeight: 1.55, color: '#1F1F2E',
            }}>
              <div style={{ fontStyle: 'normal' }}>{bridge}</div>
              {bridgeAttribution && (
                <div style={{
                  marginTop: 10, fontSize: 14, color: '#6B6B6B', fontStyle: 'normal',
                }}>{bridgeAttribution}</div>
              )}
            </blockquote>
          )}

          {offer && <OfferCard offer={offer} />}

          {ctas.length > 0 && offer && (
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1F1F2E', margin: '28px 0 12px' }}>
              Rather talk it through first?
            </div>
          )}

          {ctas.length > 0 && (
            <div style={{
              display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start',
            }}>
              {ctas.map((cta, i) => (
                <div key={i} style={{ flex: '1 1 280px', minWidth: 240 }}>
                  <a
                    href={cta.url}
                    target="_blank"
                    rel="noreferrer"
                    // With an offer on the page, the offer is the primary
                    // action and every button below it is secondary.
                    style={i === 0 && !offer ? primaryCtaStyle : secondaryCtaStyle}
                  >
                    {cta.label}
                  </a>
                  {cta.hint && (
                    <div style={{
                      marginTop: 8, fontSize: 13, color: '#6B6B6B', lineHeight: 1.4,
                      textAlign: 'center',
                    }}>
                      {cta.hint}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {rp.blocksBelow.length > 0 && (
        <div style={{ marginTop: 36 }}>
          {(rp.blocksBelow as ContentBlock[]).map((b, i) => <Block key={i} block={b} />)}
        </div>
      )}
    </>
  );
}

/** The event's own offer — set per event in admin (Replay Control → Replay
 *  page), never hard-coded. The worker only sends it while it is open. */
function OfferCard({ offer }: { offer: ReplayOffer }) {
  return (
    <div style={{
      background: '#0E1E3A', color: '#FFFFFF', borderRadius: 16,
      padding: '30px 30px 26px', boxShadow: '0 12px 32px rgba(14,30,58,0.18)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{
          fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: '#F7B267',
        }}>{offer.eyebrow}</span>
        {offer.valueLabel && (
          <span style={{
            fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
            background: 'var(--color-primary, #F05A28)', color: '#FFFFFF',
          }}>{offer.valueLabel}</span>
        )}
      </div>
      <div style={{
        fontFamily: 'Fraunces, Georgia, serif', fontSize: 30, fontWeight: 600,
        lineHeight: 1.15, margin: '0 0 12px',
      }}>{offer.headline}</div>
      {offer.body && (
        <p style={{ fontSize: 16, lineHeight: 1.55, color: 'rgba(255,255,255,0.85)', margin: '0 0 18px', maxWidth: 640 }}>
          {offer.body}
        </p>
      )}
      {offer.endsLabel && (
        <div style={{ fontSize: 14, fontWeight: 600, color: '#F7B267', margin: '0 0 18px' }}>
          Offer ends {offer.endsLabel}
        </div>
      )}
      <a
        href={offer.ctaUrl}
        target="_blank"
        rel="noreferrer"
        style={{ ...primaryCtaStyle, display: 'inline-block', width: 'auto', minWidth: 260 }}
      >
        {offer.ctaLabel}
      </a>
      {offer.hint && (
        <div style={{ marginTop: 10, fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>
          {offer.hint}
        </div>
      )}
    </div>
  );
}

/** The direct-visit gate: the page does not know the visitor yet, so it says
 *  what this replay is (title, when it was recorded, who is in it, how long it
 *  stays up, the poster) beside the email form, instead of a lone form on an
 *  empty page. Stacks on a phone. */
function GateLayout({ shell, slug, children }: { shell: BrandShell; slug: string; children: React.ReactNode }) {
  const [posterOk, setPosterOk] = useState(true);
  const headline = shell.gateHeadline ?? shell.title ?? 'Watch the replay';
  const lede = shell.gateSubheading ?? shell.lede ?? shell.heroSubhead;
  const hosts = ((shell.hostBlock as unknown as { hosts?: { name?: string; role?: string; photoUrl?: string }[] } | null)?.hosts ?? [])
    .filter((h) => h?.name).slice(0, 3);
  const meta = [
    shell.recordedOn ? `Recorded live ${shell.recordedOn}` : null,
    shell.duration,
    shell.closesOn ? `Available through ${shell.closesOn}` : null,
  ].filter(Boolean).join(' · ');
  const offer = shell.offer;
  return (
    <div className="replay-gate-grid">
      <style>{`
        .replay-gate-grid { display: grid; gap: 24px; margin-top: 32px; align-items: start;
          grid-template-areas: "head" "form" "detail"; }
        .replay-gate-head { grid-area: head; }
        .replay-gate-form { grid-area: form; }
        .replay-gate-detail { grid-area: detail; }
        @media (min-width: 820px) {
          .replay-gate-grid { grid-template-columns: 1.15fr 1fr; column-gap: 32px; row-gap: 0; margin-top: 56px;
            grid-template-areas: "head form" "detail form"; }
        }
      `}</style>
      {/* On a phone the email box follows the poster, so a visitor never has
          to scroll past the description to find how to watch. */}
      <div className="replay-gate-head">
        <div style={{
          fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'var(--color-primary, #F05A28)', marginBottom: 10,
        }}>Replay</div>
        <h1 style={{
          fontFamily: 'Fraunces, Georgia, serif', fontSize: 36, fontWeight: 600,
          lineHeight: 1.1, margin: '0 0 10px',
        }}>{headline}</h1>
        {meta && <div style={{ fontSize: 14, color: '#6B6B6B', marginBottom: 18 }}>{meta}</div>}
        <div style={{
          position: 'relative', paddingTop: '56.25%', borderRadius: 12, overflow: 'hidden',
          background: '#0E1E3A', border: '1px solid #E5E3DF', marginBottom: 18,
        }}>
          {posterOk && slug && (
            <img
              src={`${API}/api/public/replays/${encodeURIComponent(slug)}/poster`}
              alt=""
              onError={() => setPosterOk(false)}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.7)' }}
            />
          )}
          <div aria-hidden="true" style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 10, color: '#FFFFFF',
          }}>
            <span style={{
              width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,0,0,0.5)',
              border: '2px solid rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, paddingLeft: 4, boxSizing: 'border-box',
            }}>▶</span>
          </div>
        </div>
      </div>
      <div className="replay-gate-detail">
        {lede && <p style={{ fontSize: 16, lineHeight: 1.55, color: '#3F3F46', margin: '0 0 18px' }}>{lede}</p>}
        {hosts.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 18 }}>
            {hosts.map((h, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {h.photoUrl && (
                  <img src={h.photoUrl} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                )}
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1F1F2E' }}>{h.name}</div>
              </div>
            ))}
          </div>
        )}
        {offer && (
          <div style={{
            borderLeft: '3px solid var(--color-primary, #F05A28)', padding: '4px 0 4px 14px',
            fontSize: 14, lineHeight: 1.5, color: '#1F1F2E',
          }}>
            <strong>Watching the replay?</strong> {offer.headline}
            {/* Skip the badge when the headline already says it
                ("A $5,000 program, included ($5,000 value)" reads twice). */}
            {offer.valueLabel && !offer.headline.includes(offer.valueLabel.split(' ')[0]) ? ` (${offer.valueLabel})` : ''}
            {offer.endsLabel ? `, until ${offer.endsLabel}.` : '.'}
          </div>
        )}
      </div>
      <div className="replay-gate-form">{children}</div>
    </div>
  );
}

const primaryCtaStyle: React.CSSProperties = {
  display: 'block', padding: '16px 24px', fontSize: 16, fontWeight: 600,
  background: 'var(--color-primary, #F05A28)', color: 'white',
  border: 'none', borderRadius: 10, textDecoration: 'none', textAlign: 'center',
  width: '100%', boxSizing: 'border-box',
};

const secondaryCtaStyle: React.CSSProperties = {
  display: 'block', padding: '15px 24px', fontSize: 15, fontWeight: 600,
  background: 'transparent', color: '#1F1F2E',
  border: '1.5px solid #D1D1D6', borderRadius: 10, textDecoration: 'none',
  textAlign: 'center', width: '100%', boxSizing: 'border-box',
};

function VideoFrame({
  signedIframeUrl,
  externalUrl,
  thumbnailUrl,
  title,
  iframeRef,
}: {
  signedIframeUrl: string | null
  externalUrl: string | null
  thumbnailUrl: string | null
  title: string
  iframeRef?: React.RefObject<HTMLIFrameElement>
}) {
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    paddingTop: '56.25%',
    background: '#000',
    borderRadius: 12,
    overflow: 'hidden',
    border: '1px solid #E5E3DF',
  };
  if (signedIframeUrl) {
    return (
      <div style={containerStyle}>
        <iframe
          ref={iframeRef}
          src={signedIframeUrl}
          loading="lazy"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          title={title}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%', border: 'none',
          }}
        />
      </div>
    );
  }
  if (externalUrl) {
    // External fallback — don't try to embed (cross-origin frame may be blocked).
    // Render a poster + click-out card instead.
    return (
      <a href={externalUrl} target="_blank" rel="noreferrer" style={{
        ...containerStyle, display: 'block', textDecoration: 'none', cursor: 'pointer',
      }}>
        {thumbnailUrl && (
          <img src={thumbnailUrl} alt={title} style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
          }} />
        )}
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.4)', color: 'white',
          fontSize: 18, fontWeight: 600, gap: 12,
        }}>
          ▶ Watch replay (opens in new tab)
        </div>
      </a>
    );
  }
  return null;
}

